// Sugalarm — Cycle 351: the FIRE-TIME predicate for the cycle-232
// sustained high-side chime cadence.
//
// WHY THIS EXISTS (operator incident, 2026-07-19 05:53 CDT / 10:53 UTC):
// the app was opened on an iPhone and sounded the member's real high alarm
// tone while their live reading was 110 and in range. The live DB shows no
// alert row, no prediction, and no notification_delivery of any rail for
// that member that day — no server-driven path could have produced it.
//
// The cycle-232 cadence arms a 60-minute `setInterval` while a member sits
// high-side, and the callback re-checked ONLY `alerts_enabled`. It never
// re-evaluated the conditions it was armed on. iOS WKWebView / Android
// WebView SUSPEND JS timers while backgrounded and fire the overdue
// callback on foreground (the same webview behavior cycle 309 documented
// for the auto-announce cadence) — so an interval armed during a real high
// could fire its chime at app-open long after the member had recovered,
// against a reading that is now fresh and in range.
//
// This is the SAME lesson the server learned on the repeating rails in
// cycles 346 and 349: a repeating alarm rail must re-evaluate the member's
// CURRENT state at SEND time, never trust the state captured when it armed
// (see `_sweep_voice_blitz` / `_sweep_contact_sms`). The value an alarm
// asserts must be the member's situation RIGHT NOW.
//
// Cycle 474 built the named follow-up: the cross-surface emit decision
// (`requestCue`, at the end of this file) that every audible and spoken
// surface consults at FIRE time. `mayEmitHighCadenceCue` stays as the
// high cadence's own state predicate, evaluated inside that decision's
// caller exactly as before.

import type { Zone } from "./types";
import { isSettling, lastResumeAt } from "./appLifecycle";

/** The platform `Zone` (which includes `"unknown"`), or null when there is
 *  no reading at all. Both non-high values are silent by construction. */
export type CueZone = Zone | null;

export interface HighCadenceCueState {
  /** The member's `alerts_enabled` setting. */
  enabled: boolean;
  /** The most recent reading is inside the provider-aware staleness
   *  window. A dead sensor never sounds (stale → neutral). */
  readingFresh: boolean;
  /** Zone of the CURRENT reading, evaluated against current thresholds. */
  zone: CueZone;
  /** The episode is live and unmuted (`phase === "active" && !silenced`;
   *  false while snoozed, stopped, or recovered). Null = no episode. */
  episodeAudioOn: boolean | null;
}

/** True only when a sustained high-side chime is legitimate RIGHT NOW.
 *
 *  Evaluated at BOTH arm time and fire time. A real, still-live high
 *  satisfies it at fire time exactly as it did at arm time, so a genuine
 *  high is never delayed, weakened, or muted — the predicate only refuses
 *  a chime whose justification has already ended. */
export function mayEmitHighCadenceCue(s: HighCadenceCueState): boolean {
  return (
    s.enabled &&
    s.readingFresh &&
    (s.zone === "high" || s.zone === "urgent_high") &&
    s.episodeAudioOn === true
  );
}

// ─── Cycle 429: the ONE age guard every event cue reads ───────────────
//
// The high chime had a staleness guard since cycle 112 (ten minutes on the
// row's `fired_at`); no other cue had one. On 2026-09-10 a socket that
// survived an iOS freeze delivered its whole backlog in one burst on
// foreground, and every buffered event fired its cue — thirteen crossings
// and five recoveries in a few hundred milliseconds. Now every EVENT cue
// (a crossing chime, the recovery cue, the return-to-range cue, the
// prediction sound) plays only for an event whose own timestamp is within
// `CUE_EVENT_AGE_MS` of now. STATE cues — the low loop, the kiosk's
// repeating urgent cue — are deliberately NOT gated here: they reflect
// what is sounding now, and a device that reconnects mid-episode must
// still sound (cycle 231). Thirty seconds is far past the fan-out's ~2 s
// and far short of the next reading.

export const CUE_EVENT_AGE_MS = 30_000;

/** Is an event fresh enough to be cued? A missing or unparsable stamp is
 *  NOT fresh — silence is the safe direction for a one-shot cue whose
 *  event we cannot date (the alarm's persistence lives in the loops, the
 *  pushes and the calls, never in a one-shot). */
export function cueEventFresh(
  stamp: string | number | null | undefined,
  nowMs: number,
  boundMs: number = CUE_EVENT_AGE_MS,
): boolean {
  if (stamp == null) return false;
  const ms = typeof stamp === "number" ? stamp : new Date(stamp).getTime();
  if (!Number.isFinite(ms)) return false;
  return nowMs - ms <= boundMs;
}

// ─── Cycle 474: THE ONE EMIT DECISION ─────────────────────────────────
//
// ⚠ EVERY AUDIBLE OR SPOKEN CUE GOES THROUGH `requestCue`, AT FIRE TIME.
// The alarm elements reach it through `useAlarmSounds`' `safePlay` /
// `safePlayReporting` (each element carries its kind from creation), TTS
// through `useTTS`, the kiosk wake earcon through `lib/earcon.ts`. A
// direct `play()` on a cue element anywhere else is a defect.
//
// The decision, in order:
//   1. SETTLING (lib/appLifecycle): dropped, never deferred. A tap the user
//      just made (`runUserInitiated`: Settings previews, Test alarms) is
//      exempt — it is not pre-resume state.
//   2. HIDDEN, on a NATIVE PHONE SHELL (the "mobile" layout inside the iOS
//      or Android standard shell — never a browser, safety review round 1):
//      dropped. In the
//      background native push and the native ringer own sound; a play()
//      issued from a hidden WebView can be held and started on resume
//      alongside fresh ones. The KIOSK and the DESKTOP browser never take
//      this branch (a cycle-473 Wi-Fi page covering the kiosk must not mute
//      an alarm; a desktop tab plays background audio and has no native
//      ringer behind it — regression-guard round 1).
//   3. ONE VOICE AT A TIME: a request while a cue of EQUAL OR HIGHER
//      priority is sounding is dropped (never queued); an allowed request
//      PREEMPTS every lower one that is sounding.
//
// The priority order (high → low) — "lows kill, highs do not" (cycle 311):
//   urgent_low > low > prediction (a predicted LOW) > no_data
//   > urgent_high > high > preview > aux_tick > reminder > in_range > tts > earcon
// A predicted low outranks the dark-feed tone and the highs because it is
// low-side; the dark-feed tone outranks the highs because a dark feed can
// hide a low. Reminder, in-range/recovery and speech are the lowest, as the
// cycle-474 brief requires.

export type CueKind =
  | "urgent_low"
  | "low"
  | "prediction"
  | "no_data"
  | "urgent_high"
  | "high"
  | "preview"
  | "aux_tick"
  | "reminder"
  | "in_range"
  | "tts"
  | "earcon";

export const CUE_PRIORITY: Readonly<Record<CueKind, number>> = {
  urgent_low: 100,
  low: 90,
  prediction: 80,
  no_data: 70,
  urgent_high: 60,
  high: 50,
  preview: 40,
  aux_tick: 30,
  reminder: 20,
  in_range: 15,
  tts: 10,
  earcon: 5,
};

export type CueSuppression = "settling" | "hidden" | "outranked" | "stale_queue";

export type CueDecision = { allow: true } | { allow: false; reason: CueSuppression };

/** Something that can sound: an alarm element, the TTS voice. */
export interface CueSource {
  kind: CueKind;
  /** Audible right now. */
  sounding(): boolean;
  /** Stop it because a higher cue is starting. */
  preempt(): void;
}

const sources = new Set<CueSource>();

export function registerCueSource(src: CueSource): () => void {
  sources.add(src);
  return () => {
    sources.delete(src);
  };
}

let surfacePhone = false;
/** Set by the alarm hook: the "mobile" layout inside a native shell.
 *  Defaults to NOT a phone: the hidden gate is off unless a phone says so. */
export function setCuePhoneSurface(phone: boolean): void {
  surfacePhone = phone;
}

let userInitiatedDepth = 0;
/** Is a user-initiated scope open right now? Captured at REQUEST time by a
 *  cue that fires later (a play queued behind the unlock prime, speech that
 *  waits for its fetch), so the exemption survives to the fire-time check. */
export function userInitiatedNow(): boolean {
  return userInitiatedDepth > 0;
}
/** Run `fn` as the direct consequence of a user's tap: exempt from the
 *  settle drop (still subject to the hidden gate and the arbiter). */
export function runUserInitiated<T>(fn: () => T): T {
  userInitiatedDepth += 1;
  try {
    return fn();
  } finally {
    userInitiatedDepth -= 1;
  }
}

// ── telemetry (deliverable 8): a sink the app shell installs; tests leave
// it unset and nothing is sent. Bounded fields only — never a value. ──
export interface CueTelemetry {
  kind: CueKind;
  outcome: "emitted" | CueSuppression;
  inSettle: boolean;
  msSinceResume: number | null;
  surface: "phone" | "fixed";
}
let telemetrySink: ((e: CueTelemetry) => void) | null = null;
export function setCueTelemetrySink(fn: ((e: CueTelemetry) => void) | null): void {
  telemetrySink = fn;
}

function documentHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function report(kind: CueKind, outcome: CueTelemetry["outcome"], inSettle: boolean): void {
  if (!telemetrySink) return;
  const r = lastResumeAt();
  try {
    telemetrySink({
      kind,
      outcome,
      inSettle,
      msSinceResume: Number.isFinite(r) ? Math.max(0, Date.now() - r) : null,
      surface: surfacePhone ? "phone" : "fixed",
    });
  } catch {
    /* telemetry never disturbs a cue */
  }
}

/** ⚠ A user's own tap competes at PREVIEW priority, whatever element it
 *  plays through (safety review round 1, F1): a Settings Test "Urgent Low"
 *  plays the REAL urgent-low element, and at priority 100 it outranked a
 *  real LOW starting during its clip — the loop was dropped and the episode
 *  stayed silent. A tap may never outrank or preempt a real cue. */
function effectivePriority(kind: CueKind): number {
  return userInitiatedDepth > 0 ? Math.min(CUE_PRIORITY[kind], CUE_PRIORITY.preview) : CUE_PRIORITY[kind];
}

/** Pure part of the decision (no side effects): settle → hidden → arbiter. */
export function decideCue(kind: CueKind, self: CueSource | null = null): CueDecision {
  const settling = isSettling();
  if (settling && userInitiatedDepth === 0) return { allow: false, reason: "settling" };
  if (surfacePhone && documentHidden()) return { allow: false, reason: "hidden" };
  const p = effectivePriority(kind);
  for (const s of sources) {
    if (s === self) continue;
    if (CUE_PRIORITY[s.kind] >= p && s.sounding()) return { allow: false, reason: "outranked" };
  }
  return { allow: true };
}

/** THE emit gate. Decides, preempts lower cues on an allow, reports either
 *  way. Call it at FIRE time, immediately before the play/speak. */
export function requestCue(
  kind: CueKind,
  self: CueSource | null = null,
  userInitiated: boolean = false,
): CueDecision {
  if (userInitiated && userInitiatedDepth === 0) return runUserInitiated(() => requestCue(kind, self));
  const inSettle = isSettling();
  const d = decideCue(kind, self);
  if (!d.allow) {
    report(kind, d.reason, inSettle);
    return d;
  }
  const p = effectivePriority(kind);
  for (const s of [...sources]) {
    if (s === self) continue;
    if (CUE_PRIORITY[s.kind] < p && s.sounding()) {
      try {
        s.preempt();
      } catch {
        /* a failed preempt never blocks the higher cue */
      }
    }
  }
  report(kind, "emitted", inSettle);
  return d;
}

/** Deliverable 5: may a cue QUEUED at `queuedAtMs` (while `wasVisible`) be
 *  replayed now? Only one queued while visible, after the newest resume. */
export function queuedCueStillValid(queuedAtMs: number, wasVisible: boolean): boolean {
  if (!wasVisible) return false;
  return queuedAtMs >= lastResumeAt();
}

/** Record a suppression decided outside `requestCue` (a queue aged out). */
export function reportCueSuppressed(kind: CueKind, reason: CueSuppression): void {
  report(kind, reason, isSettling());
}
